/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useState} from 'react'
import {useToast} from '@app/hooks/use-toast'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useProduct} from '@salesforce/commerce-sdk-react'
import useShipToCountry from './use-ship-to-country'

/**
 * This hook is responsible for fetching a product detail based on the variation selection
 * @param initialProduct - the initial product when the modal is first open
 * @returns object
 */
export const useProductViewModal = (initialProduct) => {
    const intl = useIntl()
    const toast = useToast()
    const [productId, setProductId] = useState(initialProduct?.id)
    const {shipToCountry} = useShipToCountry()

    const {data: product, isFetching} = useProduct(
        {
            parameters: {
                id: productId,
                allImages: true, // Swatches don't work without this!
                c_country: shipToCountry?.id
            }
        },
        {
            placeholderData: initialProduct,
            keepPreviousData: true,
            onError: () => {
                toast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }
        }
    )

    return {
        product,
        isFetching,
        onProductIdChange: setProductId
    }
}
